import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { getInstallations } from "../unitEventsUtils";
import {
  deriveTenure,
  TENURE_FETCH_FAILED,
  TENURE_UNRESOLVED,
  type TenureScope,
} from "../../../services/installationTenure";

/**
 * A sensor's current-location cutoff, for gating its historical telemetry.
 *
 * Every consumer shares the key `["installations", name]`, so walking
 * portfolio -> property -> sensor costs one installations read per sensor for
 * the whole session, not one per screen.
 *
 * Long-cached and never polled: an install is a rare, human-initiated event, and
 * a re-home shows up on the next stale read. When it does, the cutoff moves, the
 * scope key moves with it (see `scopeKeyOf`) and the stale telemetry generation
 * becomes unreachable rather than needing manual invalidation.
 */

const STALE_TIME = 10 * 60_000;
const GC_TIME = 2 * 60 * 60_000;
/**
 * No retries, against react-query's default of three.
 *
 * This read GATES the historical fan-out for every sensor on screen, so each
 * retry stalls the whole page's counts behind it -- forty sensors on a portfolio
 * page, and worst of all in exactly the case retries are supposed to help with:
 * an environment where `/api/installations` is not deployed and every attempt
 * will fail. Failing open immediately and saying so is the better answer, and
 * the scope falls back to the full history either way.
 */
const RETRY = false;

export type SensorTenure = {
  scope: TenureScope;
  /**
   * True once we know as much as we are going to know -- on success AND on
   * failure. Dependent queries gate on this, so a failed installations read must
   * not stall the page: it falls open to the full history and says so.
   */
  settled: boolean;
};

const UNRESOLVED: SensorTenure = { scope: TENURE_UNRESOLVED, settled: false };

/**
 * A FAILED read is not a sensor with no installation record.
 *
 * The same distinction the report hooks make about connectivity: an empty answer
 * says "no record", which we report honestly as unknown, while a failure that
 * arrived looking empty would be a different claim -- and here it would put the
 * words "no installation record" on screen about a sensor that may well have
 * one. So the two land on different `reason`s and the UI words them differently.
 */
function toTenure(
  status: "pending" | "error" | "success",
  payload: { data?: unknown } | undefined
): SensorTenure {
  if (status === "error") return { scope: TENURE_FETCH_FAILED, settled: true };
  if (status !== "success") return UNRESOLVED;
  return { scope: deriveTenure(payload?.data), settled: true };
}

const options = (name: string) => ({
  queryKey: ["installations", name],
  queryFn: async () => await getInstallations(name),
  enabled: !!name,
  staleTime: STALE_TIME,
  gcTime: GC_TIME,
  retry: RETRY,
  refetchOnWindowFocus: false,
});

export function useSensorTenure(sensorName: string): SensorTenure {
  const { status, data } = useQuery(options(sensorName));
  return useMemo(
    () => (sensorName ? toTenure(status, data) : UNRESOLVED),
    [sensorName, status, data]
  );
}

/**
 * The same thing for a building's worth of sensors. Returns a Map so callers
 * index by name rather than relying on array position, which the sensors table
 * and the aggregates hook derive independently.
 */
export function useSensorTenures(sensorNames: string[]): Map<string, SensorTenure> {
  const results = useQueries({
    queries: sensorNames.map((name) => options(name)),
  });

  // The memo-signature idiom used by the other fan-out hooks: react-query hands
  // back a fresh array each render, so memoise on the statuses and stamps.
  const signature = results
    .map((q) => `${q.status}:${q.dataUpdatedAt}`)
    .join("|");

  return useMemo(() => {
    const map = new Map<string, SensorTenure>();
    sensorNames.forEach((name, index) => {
      const q = results[index];
      map.set(name, q ? toTenure(q.status, q.data) : UNRESOLVED);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, sensorNames.join("|")]);
}
